import { useUser } from "@clerk/react";
import { useOnboardingProfile } from "../../onboardingStore";
import { cn } from "../../lib/utils";
import { resolveOrganizationLogoUrl } from "../onboarding/MorrowOnboarding.logic";
import { useI18n } from "~/i18n";
import {
  clerkPortraitImageUrl,
  MorrowPersonAvatar,
  type PersonAvatarSize,
} from "../workspace/MorrowPersonCard";
import { useUserProfile } from "../workspace/morrowUserProfileStore";

/** Onboarding-stored company logo — used when Clerk has no org image. */
export function useCompanyLogoFallback(): string {
  return useOnboardingProfile().companyLogoUrl.trim();
}

/** Onboarding company logo when present, otherwise the Clerk org logo. */
export function useResolvedOrganizationLogoUrl(organization: unknown): string {
  const fallback = useCompanyLogoFallback();
  return resolveOrganizationLogoUrl(organization, fallback);
}

/** Sidebar account glyph: always the signed-in user's portrait. */
export function SidebarWorkspaceAvatar({
  userName,
  userImageUrl,
  avatarSeed,
  size = "xs",
  className,
}: {
  readonly userName: string;
  readonly userImageUrl?: string;
  readonly avatarSeed?: string;
  readonly size?: PersonAvatarSize;
  readonly className?: string;
}) {
  return (
    <MorrowPersonAvatar
      name={userName}
      {...(userImageUrl ? { imageUrl: userImageUrl } : {})}
      {...(avatarSeed ? { avatarSeed } : {})}
      size={size}
      {...(className ? { className } : {})}
    />
  );
}

/** User portrait for "Personal account" rows in org switch menus. */
export function PersonalAccountAvatar({
  size = "sm",
  className,
}: {
  readonly size?: PersonAvatarSize;
  readonly className?: string;
}) {
  const { t } = useI18n();
  const { user } = useUser();
  const userProfile = useUserProfile();
  const userName =
    user?.fullName?.trim() ||
    user?.primaryEmailAddress?.emailAddress?.trim() ||
    t("settings.organization.personal");
  const userImageUrl = clerkPortraitImageUrl(user);

  return (
    <SidebarWorkspaceAvatar
      userName={userName}
      {...(userImageUrl ? { userImageUrl } : {})}
      {...(userProfile.avatarSeed ? { avatarSeed: userProfile.avatarSeed } : {})}
      size={size}
      {...(className ? { className } : {})}
    />
  );
}

/** Square org avatar: the real Clerk logo when present, initials otherwise. */
export function OrganizationAvatar({
  logoUrl,
  name,
  className,
}: {
  readonly logoUrl: string;
  readonly name: string;
  readonly className?: string;
}) {
  if (logoUrl) {
    return (
      <img
        alt=""
        src={logoUrl}
        className={cn("size-6 shrink-0 rounded-lg object-cover", className)}
      />
    );
  }
  const initials =
    name
      .split(/\s+/)
      .map((word) => word.charAt(0))
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?";
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-lg bg-muted text-[10px] font-semibold text-muted-foreground",
        className,
      )}
    >
      {initials}
    </span>
  );
}
